import React, { FC, useState } from "react"
import type { FieldProps } from "@rjsf/core"
import { Table } from "resha"
import { Button, Markdown, OutlinedButton, Text, Title } from "dash-styled"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"

import { useBoolean } from "../../../../Hooks/useBoolean"
import {
    DocumentModal,
    DocumentModalOptions,
    DocumentModalType,
    DocumentType
} from "../DocumentClassification/DocumentModal"
import { SchemaFormDeleteModal } from "../Modals"

export const ClassificationParamsField: FC<FieldProps> = ({
    schema,
    formData,
    onChange
}) => {
    const { choices }: { choices: DocumentType[] } = formData
    const [showDocumentModal, openDocumentModal, closeDocumentModal] =
        useBoolean(false)
    const [{ index: docIndex, type: docType }, setDocumentModalOptions] =
        useState<DocumentModalOptions>({})
    const [showDeleteModal, openDeleteModal, closeDeleteModal] =
        useBoolean(false)

    const handleOpenDocumentModal = () => {
        setDocumentModalOptions({
            index: undefined,
            type: DocumentModalType.New
        })
        openDocumentModal()
    }

    const handleEditDocument = (options: DocumentModalOptions) => () => {
        setDocumentModalOptions(options)
        openDocumentModal()
    }

    const handleDocumentModalSubmit = (field: any) => {
        const documents = [...choices]
        if (docType === DocumentModalType.Edit && docIndex !== undefined) {
            documents.splice(docIndex, 1, field)
        } else {
            documents.push(field)
        }
        onChange({
            choices: documents
        })
    }

    const handleDeleteOpen = (index: number) => () => {
        setDocumentModalOptions({
            index
        })
        openDeleteModal()
    }

    const handleDeleteDocument = (index: number) => () => {
        const documents = [...choices]
        documents?.splice(index, 1)
        onChange({
            choices: documents
        })
    }

    return (
        <>
            <Title
                color="darkviolet"
                variant="heading2"
                component="h3"
                weight="semibold"
            >
                {schema.title ?? ""}
            </Title>
            {schema?.description && (
                <Markdown
                    className="text-sm text-smoke-500 mb-2"
                    text={schema?.description}
                />
            )}
            <div className="mb-4 border border-solid rounded-lg">
                <div className="flex items-center bg-white p-4.5 pl-6 rounded-t-lg shadow">
                    <Title
                        component="h4"
                        variant="heading4"
                        className="capitalize w-full m-0"
                    >
                        Document types
                    </Title>
                </div>
                <div className="bg-smoke-20 p-6">
                    <div className="flex mb-4">
                        <OutlinedButton
                            testId="add-new-document-type"
                            Icon={AddIcon}
                            label="Add new document type"
                            marginLeft="2"
                            className="bg-smoke-20 py-3"
                            onClick={handleOpenDocumentModal}
                        />
                    </div>
                    <Table
                        className="w-full"
                        rowBgColor=""
                        style={{
                            borderSpacing: "0 4px"
                        }}
                        getKey={({ instruction, name, advices }) =>
                            `${instruction}-${name}-${advices.join(",")}`
                        }
                        items={choices}
                        withBorders={false}
                        columns={[
                            {
                                className:
                                    "w-6/12 rounded-l-xl pl-4 bg-white border-b-rhino-300",
                                renderHeader: () => (
                                    <Text color="gray">Name</Text>
                                ),
                                headerClassName: "border-b-0",
                                render: ({ name }) => {
                                    return (
                                        <Title
                                            component="h4"
                                            variant="heading4"
                                            className="capitalize m-0"
                                        >
                                            {name}
                                        </Title>
                                    )
                                }
                            },
                            {
                                className:
                                    "w-6/12 rounded-r-xl pr-2 bg-white border-b-rhino-300",
                                renderHeader: () => null,
                                render: (value, isChecked, index) => {
                                    return (
                                        <div className="flex items-center justify-end">
                                            <OutlinedButton
                                                Icon={EditIcon}
                                                label="Edit"
                                                className="py-3 bg-white"
                                                onClick={handleEditDocument({
                                                    index,
                                                    type: DocumentModalType.Edit
                                                })}
                                            />
                                            <Button
                                                testId="delete-document-button"
                                                variant="contained"
                                                color="plain"
                                                marginLeft="2"
                                                className={`py-3 px-3 bg-white items-center flex w-max-content`}
                                                onClick={handleDeleteOpen(
                                                    index
                                                )}
                                            >
                                                <TrashIcon />
                                            </Button>
                                        </div>
                                    )
                                }
                            }
                        ]}
                    />
                </div>
                {showDocumentModal && (
                    <DocumentModal
                        type={docType}
                        schema={schema}
                        onSubmit={handleDocumentModalSubmit}
                        onClose={closeDocumentModal}
                        index={
                            docType === DocumentModalType.Edit &&
                            docIndex !== undefined
                                ? docIndex
                                : choices.length
                        }
                        value={
                            docIndex !== undefined
                                ? choices[docIndex]
                                : undefined
                        }
                    />
                )}
                {showDeleteModal && (
                    <SchemaFormDeleteModal
                        testId="delete-modal"
                        onClose={closeDeleteModal}
                        onSubmit={handleDeleteDocument(docIndex as number)}
                    />
                )}
            </div>
        </>
    )
}
